<template>
  <div class="avatar-menu" @click.stop>
    <Dropdown
      v-model="show"
      class="edit-dropdown"
      :menu-right="!displayMenuLeft"
      :menu-left="displayMenuLeft"
      data-test="avatars-dropdown"
    >
      <Btn
        type="button"
        class="btn btn-trigger dropdown-toggle"
        :class="{ 'dropdown-selected': show }"
        data-test="avatars-trigger-button"
      >
        {{ triggerText }}
      </Btn>
      <template #dropdown>
        <li
          v-for="user in users"
          :key="user.id"
          class="avatar-dropdown-item"
          data-test="avatars-dropdown-list-item"
        >
          <UserAvatar
            :user="user"
            size="30px"
            :variant="avatarVariant(user)"
            :tooltip-text="getTooltipText(user)"
          />
          <span :style="{ opacity: avatarVariant(user) === 'faded' ? 0.5 : 1 }">{{
            getTooltipText(user)
          }}</span>
        </li>
      </template>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { Dropdown, Btn } from "uiv";
import { computed, ref } from "vue";
import UserAvatar from "../reusables/user-avatar.vue";
import { UserProfileApiModel } from "../../api-models";
import useWindowWidth from "../../composables/useWindowWidth";

const props = defineProps<{
  users: UserProfileApiModel[];
  usersWhoHaveCompleted: UserProfileApiModel[];
  triggerText: string;
  actionType: "Pathway" | "Resource" | "Invite";
}>();

const show = ref(false);

const { width } = useWindowWidth();

const displayMenuLeft = computed(() => {
  return width.value < 552;
});

function avatarVariant(user: UserProfileApiModel) {
  if (props.actionType === "Invite") return "default";
  return props.usersWhoHaveCompleted.includes(user) ? "default" : "faded";
}

function getTooltipText(user: UserProfileApiModel) {
  if (props.actionType === "Invite") return `${user.firstName} ${user.lastName}`;
  return props.usersWhoHaveCompleted.includes(user)
    ? `${user.firstName} ${user.lastName} (completed)`
    : `${user.firstName} ${user.lastName} (incomplete)`;
}
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";
@avatar-size: 30px;
.dropdown {
  .btn.btn-trigger {
    border-radius: 100vmax !important;
    aspect-ratio: 1;
    width: @avatar-size;
    padding: 0;

    &:focus {
      box-shadow: none;
    }

    @media (max-width: 1064px) {
      width: 120px;
      height: 30px;
      aspect-ratio: unset;
    }
  }
}

.dropdown-selected {
  background-size: 100% 100%;
  text-decoration: none;
}

.avatar-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
}
</style>
<style lang="less">
.avatar-menu {
  .dropdown-menu {
    background-color: #404040;
    li {
      color: #ffffff;
      font-weight: normal;
    }
  }

  .btn-group.open .dropdown-toggle {
    box-shadow: none;
  }
}
</style>
